/**
 * Session Timeout Warning Component
 * Shows a warning dialog when session is about to expire
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { Timer, RefreshCw } from 'lucide-react';
import { useSessionManagement } from '../../hooks/useSessionManagement';
import { Trans, useTranslation } from 'react-i18next';

export function SessionTimeoutWarning() {
  const { t } = useTranslation('auth');
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  const { extendSession, getTimeRemainingFormatted, getTimeRemaining } =
    useSessionManagement({
      enabled: true,
      onWarning: () => setShowWarning(true),
      onTimeout: () => {
        setShowWarning(false);
        // Logout is handled by the hook
      },
    });

  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      const remaining = getTimeRemaining();
      if (remaining <= 0) {
        setShowWarning(false);
        return;
      }
      setTimeRemaining(getTimeRemainingFormatted());
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, getTimeRemaining, getTimeRemainingFormatted]);

  const handleExtend = () => {
    extendSession();
    setShowWarning(false);
  };

  const progressValue = () => {
    const remaining = getTimeRemaining();
    const fiveMinutes = 5 * 60 * 1000;
    return ((fiveMinutes - remaining) / fiveMinutes) * 100;
  };

  return (
    <Dialog open={showWarning} disableEscapeKeyDown>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timer size={24} />
          {t('session.expiringSoon')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          <Trans
            i18nKey="session.willExpire"
            ns="auth"
            values={{ time: timeRemaining }}
            components={{ b: <strong /> }}
          />
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('session.clickToContinue')}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={progressValue()} color="warning" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleExtend}
          variant="contained"
          startIcon={<RefreshCw size={16} />}
          fullWidth
        >
          {t('session.stayLoggedIn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
